import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Button } from "reactstrap";
import DownloadFileIcon from "../svg/DownloadFile";

type apiData<T extends object> = {
  data: Array<T>;
  ExcelSheetName: string;
  hideColumns?: (keyof T)[];
  order?: (keyof T)[];
  rename?: Partial<Record<keyof T, string>>;
  skipColumnsCount?: number; // skip empty columns at start
  projectName?: string;
};

const DownloadExcel = <T extends object>({
  data,
  ExcelSheetName,
  hideColumns = [],
  order,
  rename = {},
  skipColumnsCount = 0,
  projectName = "Shivnala Irrigation Project",
}: apiData<T>) => {
  const handleExport = async () => {
    if (!data || data.length === 0) return;

    const allKeys = Object.keys(data[0]) as (keyof T)[];

    // visible keys (after hide)
    let visibleKeys = allKeys.filter((k) => !hideColumns.includes(k));

    // apply order
    if (order && order.length > 0) {
      visibleKeys = [
        ...order.filter((k) => visibleKeys.includes(k)),
        ...visibleKeys.filter((k) => !order.includes(k)),
      ];
    }

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Sheet1");

    const now = new Date();
    const totalColumns = visibleKeys.length + skipColumnsCount;

    // -----------------------
    // 0. INFO ROWS
    // -----------------------
    const infoRows: ExcelJS.Row[] = [];

    // Project Name - Big Font
    const projectRow = sheet.addRow([` ${projectName}`]);
    sheet.mergeCells(`A${projectRow.number}:${sheet.getColumn(totalColumns).letter}${projectRow.number}`);
    projectRow.eachCell((cell) => {
      cell.font = { bold: true, size: 20 };
      cell.alignment = { vertical: "middle", horizontal: "center" };
    });
    infoRows.push(projectRow);

    // Empty row for spacing
    const emptyRow1 = sheet.addRow([]);
    infoRows.push(emptyRow1);

    // File Name and Generated Date - Smaller Font
    const infoRow = sheet.addRow([
      `${ExcelSheetName}`,
      `Generated Date: ${now.toLocaleString()}`,
    ]);
    sheet.mergeCells(`A${infoRow.number}:${sheet.getColumn(totalColumns).letter}${infoRow.number}`);
    infoRow.eachCell((cell) => {
      cell.font = { bold: true, size: 12 };
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    });
    infoRows.push(infoRow);

    // Optional empty row before header
    const emptyRow2 = sheet.addRow([]);
    infoRows.push(emptyRow2);

    const infoRowsCount = infoRows.length;

    // -----------------------
    // 1. HEADER ROW
    // -----------------------
    const prefix = Array(skipColumnsCount).fill("");
    const headerTitles = visibleKeys.map((key) => rename[key] || (key as string));
    const header = sheet.addRow([...prefix, ...headerTitles]);

    header.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      if (colNumber <= skipColumnsCount) return;
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "4472C4" } };
      cell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // -----------------------
    // 2. DATA ROWS
    // -----------------------
    data.forEach((item) => {
      const rowValues = visibleKeys.map((key) => item[key]);
      const row = sheet.addRow([...prefix, ...rowValues]);

      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        if (colNumber <= skipColumnsCount) return;
        cell.border = {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    // -----------------------
    // 3. AUTO COLUMN WIDTH
    // -----------------------
    sheet.columns.forEach((col, index) => {
      if (!col || typeof col.eachCell !== "function") return;

      let maxLength = 0;

      col.eachCell({ includeEmpty: true }, (cell, rowNumber) => {
        if (rowNumber <= infoRowsCount) return; // skip info rows
        let value = cell.value ? cell.value.toString() : "";
        const length = value.length;
        if (length > maxLength) maxLength = length;
      });

      if (index < skipColumnsCount) {
        col.width = 3; // narrow prefix
      } else {
        col.width = Math.min(Math.max(maxLength + 2, 10), 50); // min 10, max 50
      }
    });

    // -----------------------
    // 4. DOWNLOAD FILE
    // -----------------------
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `${ExcelSheetName}.xlsx`
    );
  };

  return (
    <Button onClick={handleExport} color="primary" className="download-button px-2">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "20px",
          height: "20px",
          gap: "5px",
        }}
      >
        <DownloadFileIcon />
      </div>
    </Button>
  );
};

export default DownloadExcel;
